import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraSelect } from "../../../components/AtiraSelect";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { Modal } from "../../../components/modal/Modal";
import { EntryKind } from "../../../model/entry/types/EntryKind.enum";
import { entryActions } from "../../../redux/entry/entry.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { entryCreateEditDuplicateErrorHandler } from "../utils/entryCreateEditDuplicateErrorHandler";
import { getAvailableKindUpdateOptions } from "../utils/getAvailableKindUpdateOptions";
import { useDrawersContext } from "../../tasks/drawers-context";
import { ConvertEntryDto } from "../../../model/entry/dto/ConvertEntryDto";
import { TestIDs } from "../../../tests/types/testIds";

const Container = styled(Flex)`
  width: 100%;
  gap: ${Spacing.m};
  justify-content: center;
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  margin: 0;
  margin-top: ${Spacing.m};
  height: 2.3rem;
`;

export const EntryConvertModal: React.FC = () => {
  const [entryKindLodaing, setEntryKindLoading] = useState(false);
  const [entryKind, setEntryKind] = useState<EntryKind | undefined>(undefined);

  const {
    entry,
    entryConvertVisible,
    setEntryConvertVisible,
    setDuplicateEntries,
    setDuplicateEntryVisible,
    setCurrentOnIgnoreAction,
    setDuplicateEntriesGroups,
  } = useDrawersContext();

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const loggedInUserId = useAppSelector(
    userSliceSelectors.selectLoggedInUserId,
  );

  const getDropDownOptions = () => {
    return getAvailableKindUpdateOptions(entry?.kind as EntryKind)
      .filter(
        (k) =>
          k.toLowerCase() !== entry?.kind?.toLowerCase() &&
          k.toLowerCase() !== entryKind?.toLowerCase(),
      )
      .map((x) => ({
        label: t(`entries.table.entry.${x?.toLowerCase()}`),
        value: x,
        "data-testid": `${TestIDs.entries.table.menu.convert.modal.dropdown.option(x)}`,
      }));
  };

  const localOnClose = () => {
    setEntryKind(undefined);
    setEntryConvertVisible(false);
  };

  const updateEntryKind = async () => {
    const dto = {
      userId: loggedInUserId!,
      kind: entryKind,
      entryId: entry!._id,
    } as ConvertEntryDto;

    try {
      setEntryKindLoading(true);
      await dispatch(entryActions.convertEntryById(dto)).unwrap();

      await dispatch(
        entryActions.getEntryById({
          entryId: entry!._id,
          userId: loggedInUserId!,
        }),
      ).unwrap();

      localOnClose();

      AtiraToast.success(t("entries.table.entries.convert.success"));
    } catch (e: any) {
      console.log(e);

      entryCreateEditDuplicateErrorHandler(
        { error: e },
        {
          setEntries: setDuplicateEntries,
          setGroups: setDuplicateEntriesGroups,
          setModal: setDuplicateEntryVisible,
          setActionModal: () => setEntryConvertVisible(false),
        },
      );
      setCurrentOnIgnoreAction(() => async () => {
        await dispatch(
          entryActions.convertEntryById({
            ...dto,
            ignoreDuplicates: true,
          }),
        );
      });
    } finally {
      setEntryKindLoading(false);
    }
  };

  if (!entry) {
    return null;
  }

  return (
    <Modal
      open={entryConvertVisible}
      onClose={localOnClose}
      title={t("entries.table.entry.convert.title")}
      destroyOnHidden
    >
      <Container>
        <Text color="darkerSub">
          {t("entries.table.entry.convert.subtitle", {
            kind: t(`entries.table.entry.${entry?.kind?.toLowerCase()}`),
          })}
        </Text>

        <AtiraSelect
          options={getDropDownOptions()}
          value={{
            label: t(
              `entries.table.entry.${entryKind?.toLowerCase() || "kind.select"}`,
            ),
            value: entryKind,
          }}
          onChange={(e: any) => setEntryKind(e)}
          className={TestIDs.entries.table.menu.convert.modal.dropdown.open}
        />

        <StyledButton
          loading={entryKindLodaing}
          disabled={!entryKind}
          onClick={updateEntryKind}
          dataTestId={TestIDs.entries.table.menu.convert.modal.button}
        >
          {t("entries.table.entry.convert.update.button")}
        </StyledButton>
      </Container>
    </Modal>
  );
};
